// <Slot> — the dispatcher. Reads the slot mapping for a given slot id and
// renders each BlockInstance into the right block component. Resolution
// order (per .design.md "Axis 6 — Slot mapping"):
//
//   1. Designer override for this slot id (per-deployment JSON; lands step 6)
//   2. Layout default mapping for this slot id
//   3. Empty → render nothing
//
// Slot itself doesn't pick the mapping — it accepts a `mapping` prop the
// layout (or host, for modal slots) provides. That keeps Slot pure and
// composable: same component dispatches a layout slot, a modal-internal
// slot, or a group's children.
//
// Unknown block kinds, references to missing actions, or unknown showWhen
// shapes degrade silently with a console.warn. Never crashes the embed —
// older Designer mappings vs. newer code is a real scenario.

import type { Fragment, ReactNode } from "react";
import { contentSourceContext, type ContentSource } from "./contentSource";
import { ActionBlock } from "./ActionBlock";
import { CatalogBlock } from "./CatalogBlock";
import { DividerBlock, LineBlock, SpacerBlock } from "./DividerBlock";
import { FormSubmitBlock } from "./FormSubmitBlock";
import { GroupBlock } from "./GroupBlock";
import { ImageBlock } from "./ImageBlock";
import { InputBlock } from "./InputBlock";
import { MaterialCollectionsBlock } from "./MaterialCollectionsBlock";
import {
  ProductImageBlock,
  ProductMaterialsBlock,
  ProductNameBlock,
  ProductParameterBlock,
  ProductPriceBlock,
  ProductQuantityBlock,
  ProductSkuBlock,
} from "./ProductRowBlocks";
import { ProductTableBlock, type ColumnDef, type GroupConfig, type SortRule } from "./ProductTableBlock";
import { SelectionSurfacesBlock } from "./SelectionSurfacesBlock";
import { SeriesDescriptionBlock, SeriesImageBlock, SeriesNameBlock } from "./SeriesTileBlocks";
import { StatBlock } from "./StatBlock";
import { TextBlock } from "./TextBlock";
import {
  evaluateShowWhen,
  type BlockHostContext,
  type BlockInstance,
} from "./types";

export interface SlotProps {
  /** Stable slot id — for debug attribute + future authoring UI keying. */
  slotId: string;
  /** Block instances to render. Empty = nothing. */
  mapping: BlockInstance[] | undefined;
  host: BlockHostContext;
  /** Optional wrapper class — most layouts pass the slot's container class. */
  className?: string;
  /** Render as a Fragment instead of a wrapping <div>. Useful when the parent
   *  already provides the container (e.g. tool rail). */
  inline?: boolean;
}

export function Slot({ slotId, mapping, host, className, inline }: SlotProps) {
  if (!mapping || mapping.length === 0) {
    return null;
  }
  const children = mapping
    .filter(b => b.enabled !== false)
    .filter(b => evaluateShowWhen(b.showWhen, host))
    .map(b => renderBlock(b, host));

  if (inline) {
    // Return a Fragment so the parent's container is the only DOM node.
    return <>{children}</>;
  }
  return (
    <div className={"sd-slot" + (className ? " " + className : "")} data-slot-id={slotId}>
      {children}
    </div>
  );
}

/**
 * Block dispatch. The one place that maps block kinds to components — adding
 * a new kind = a new case here plus an import.
 */
export function renderBlock(b: BlockInstance, host: BlockHostContext): ReactNode {
  // Universal props copied off the instance once for re-use.
  const universal = {
    themeVariant: b.themeVariant,
    className: b.className,
  };
  const p = (b.props ?? {}) as Record<string, unknown>;
  // Live content-source context (active series + selected product), read off the
  // host once and handed to every string-bearing block so each can resolve its
  // visible text through resolveContentSource without reaching into host state.
  const sourceCtx = contentSourceContext(host);

  switch (b.block) {
    case "action": {
      // actionImageAssetId is stored as a bare DImage id; build the thumbnail
      // URL here (the C# embed endpoint doesn't pre-resolve per-action assets).
      const imageAssetId = typeof p.actionImageAssetId === "string" ? p.actionImageAssetId : "";
      const base = (host.baseUrl ?? "").replace(/\/+$/, "");
      const actionImageUrl = imageAssetId
        ? `${base}/api/asset/thumbnail/${imageAssetId}/512`
        : undefined;
      return (
        <ActionBlock
          key={b.key}
          actionId={String(p.actionId ?? "")}
          actionLabel={typeof p.actionLabel === "string" ? p.actionLabel : undefined}
          actionTooltip={typeof p.actionTooltip === "string" ? p.actionTooltip : undefined}
          actionIcon={typeof p.actionIcon === "string" ? p.actionIcon : undefined}
          actionImageUrl={actionImageUrl}
          labelSource={p.actionLabelSource as ContentSource | undefined}
          tooltipSource={p.actionTooltipSource as ContentSource | undefined}
          confirmTitle={typeof p.confirmTitle === "string" ? p.confirmTitle : undefined}
          confirmMessage={typeof p.confirmMessage === "string" ? p.confirmMessage : undefined}
          confirmLabel={typeof p.confirmLabel === "string" ? p.confirmLabel : undefined}
          cancelLabel={typeof p.cancelLabel === "string" ? p.cancelLabel : undefined}
          sourceCtx={sourceCtx}
          blockProps={p}
          host={host}
          {...universal}
        />
      );
    }

    case "text":
      return (
        <TextBlock
          key={b.key}
          text={typeof p.text === "string" ? p.text : undefined}
          dvarId={typeof p.dvarId === "number" ? p.dvarId : undefined}
          variant={(p.variant as
            | "eyebrow"
            | "heading"
            | "heading-display"
            | "subtitle"
            | "body"
            | "body-multiline"
            | undefined) ?? "body"}
          source={(p.source as ContentSource | undefined) ?? "literal"}
          sourceCtx={sourceCtx}
          {...universal}
        />
      );

    case "stat":
      return (
        <StatBlock
          key={b.key}
          kind={String(p.kind ?? "count")}
          label={typeof p.label === "string" ? p.label : undefined}
          labelDvarId={typeof p.labelDvarId === "number" ? p.labelDvarId : undefined}
          labelSource={p.labelSource as ContentSource | undefined}
          unitSingular={typeof p.unitSingular === "string" ? p.unitSingular : undefined}
          unitSingularSource={p.unitSingularSource as ContentSource | undefined}
          unitPlural={typeof p.unitPlural === "string" ? p.unitPlural : undefined}
          unitPluralSource={p.unitPluralSource as ContentSource | undefined}
          sourceCtx={sourceCtx}
          host={host}
          {...universal}
        />
      );

    case "image":
      return (
        <ImageBlock
          key={b.key}
          src={typeof p.src === "string" ? p.src : null}
          alt={typeof p.alt === "string" ? p.alt : undefined}
          fit={(p.fit as "contain" | "cover" | undefined) ?? "contain"}
          {...universal}
        />
      );

    case "catalog":
      return (
        <CatalogBlock
          key={b.key}
          host={host}
          variant={p.variant as "grid" | "editorial-row" | undefined}
          specParameterKeys={
            Array.isArray(p.specParameterKeys)
              ? (p.specParameterKeys as unknown[]).filter((v): v is string => typeof v === "string")
              : undefined
          }
          {...universal}
        />
      );

    case "material-collections":
      return <MaterialCollectionsBlock key={b.key} host={host} {...universal} />;

    case "notification":
      // Authoring-only — a notification block declares a trigger + message that
      // the NotificationsHost surfaces as a toast when the trigger fires. It
      // renders nothing where it sits in the slot. Returning null avoids the
      // "unknown kind" warn when the notifications slot is previewed in focus mode.
      return null;

    case "cart-items":
      // The cart-items block is authoring-only — it carries the cart modal's
      // configuration (description parameter keys, header text) read off the
      // resolved modal:cart slot by SceneDesignerTool's cart render override.
      // The actual table is rendered by the CartModal component, so Slot
      // emits nothing here. Returning null avoids the "unknown kind" warn
      // when previewing modal:cart in focus mode.
      return null;

    case "series-image":
      return <SeriesImageBlock key={b.key} {...universal} />;
    case "series-name":
      return (
        <SeriesNameBlock
          key={b.key}
          variant={typeof p.variant === "string" ? p.variant : undefined}
          {...universal}
        />
      );
    case "series-description":
      return (
        <SeriesDescriptionBlock
          key={b.key}
          variant={typeof p.variant === "string" ? p.variant : undefined}
          {...universal}
        />
      );

    case "product-table":
      // Composable element-list table. Columns + sort + group come off the
      // block props (same JSON shape the PDF pdf-product-table uses); the
      // per-cell block lists are dispatched back through renderBlockList so
      // ProductTableBlock never imports this file (cycle).
      return (
        <ProductTableBlock
          key={b.key}
          host={host}
          columns={coerceColumns(p.columns)}
          sort={coerceSort(p.sort)}
          group={coerceGroup(p.group)}
          renderBlocks={renderBlockList}
          sourceCtx={sourceCtx}
          {...universal}
        />
      );

    case "product-image":
      return <ProductImageBlock key={b.key} {...universal} />;
    case "product-name":
      return <ProductNameBlock key={b.key} {...universal} />;
    case "product-parameter":
      return (
        <ProductParameterBlock
          key={b.key}
          parameterKey={typeof p.parameterKey === "string" ? p.parameterKey : undefined}
          {...universal}
        />
      );
    case "product-sku":
      return <ProductSkuBlock key={b.key} {...universal} />;
    case "product-quantity":
      return <ProductQuantityBlock key={b.key} {...universal} />;
    case "product-materials":
      return (
        <ProductMaterialsBlock
          key={b.key}
          layout={p.layout === "inline" ? "inline" : "lines"}
          showSurface={p.showSurface !== false}
          separator={typeof p.separator === "string" ? p.separator : undefined}
          {...universal}
        />
      );
    case "product-price":
      return (
        <ProductPriceBlock
          key={b.key}
          mode={p.mode === "unit" ? "unit" : "line-total"}
          includedAllLabel={typeof p.includedAllLabel === "string" ? p.includedAllLabel : undefined}
          includedLabel={typeof p.includedLabel === "string" ? p.includedLabel : undefined}
          {...universal}
        />
      );

    case "selection-surfaces":
      return <SelectionSurfacesBlock key={b.key} host={host} {...universal} />;

    case "divider":
      return <DividerBlock key={b.key} {...universal} />;

    case "line":
      return <LineBlock key={b.key} {...universal} />;

    case "spacer":
      return (
        <SpacerBlock
          key={b.key}
          size={(p.size as "sm" | "md" | "lg" | undefined) ?? "md"}
          {...universal}
        />
      );

    case "group":
      // Recurse: each child block goes through the same dispatcher. Children
      // can themselves include groups — nesting works to any depth, though
      // in practice one level is usually enough.
      return (
        <GroupBlock
          key={b.key}
          variant={(p.variant as "card" | "row" | "row-right" | "column" | "accent-bar" | undefined) ?? "column"}
          {...universal}
        >
          {(b.children ?? [])
            .filter(c => c.enabled !== false)
            .filter(c => evaluateShowWhen(c.showWhen, host))
            .map(c => renderBlock(c, host))}
        </GroupBlock>
      );

    case "input-text":
    case "input-email":
    case "input-textarea":
      return (
        <InputBlock
          key={b.key}
          host={host}
          name={typeof p.name === "string" ? p.name : ""}
          inputType={b.block === "input-text" ? "text" : b.block === "input-email" ? "email" : "textarea"}
          label={typeof p.label === "string" ? p.label : undefined}
          placeholder={typeof p.placeholder === "string" ? p.placeholder : undefined}
          labelSource={p.labelSource as ContentSource | undefined}
          placeholderSource={p.placeholderSource as ContentSource | undefined}
          sourceCtx={sourceCtx}
          defaultValue={typeof p.defaultValue === "string" ? p.defaultValue : undefined}
          required={p.required === true}
          rows={typeof p.rows === "number" ? p.rows : undefined}
          formId={typeof p.formId === "string" ? p.formId : undefined}
          {...universal}
        />
      );

    case "form-submit":
      return (
        <FormSubmitBlock
          key={b.key}
          host={host}
          actionId={typeof p.actionId === "string" ? p.actionId : ""}
          label={typeof p.label === "string" ? p.label : undefined}
          labelSource={p.labelSource as ContentSource | undefined}
          sourceCtx={sourceCtx}
          formId={typeof p.formId === "string" ? p.formId : undefined}
          requiredFields={typeof p.requiredFields === "string" ? p.requiredFields : undefined}
          // Operator-set extras (pdfLayoutId for render-pdf-email, etc.)
          // pass through to the action verbatim alongside the form values.
          passthrough={p}
          trailingIcon={typeof p.trailingIcon === "string" ? p.trailingIcon : undefined}
          {...universal}
        />
      );

    default: {
      // Closed enum — TypeScript catches additions at the type level. This
      // case handles runtime mismatches (older bundle vs. newer Designer
      // mapping JSON), logging once and skipping.
      const unknownKind = b.block as string;
      console.warn(`[SceneDesigner] unknown block kind "${unknownKind}"; skipping.`);
      return null;
    }
  }
}

/** Render an ordered list of block instances — the same enabled/showWhen
 *  filtering + dispatch the group case does. Injected into ProductTableBlock so
 *  its column cells / group headers dispatch through here without importing
 *  this module (which would be a cycle). */
function renderBlockList(blocks: BlockInstance[], host: BlockHostContext): ReactNode {
  return blocks
    .filter(c => c.enabled !== false)
    .filter(c => evaluateShowWhen(c.showWhen, host))
    .map(c => renderBlock(c, host));
}

// ── product-table prop coercion ────────────────────────────────────────────
// The slot mapping is loosely-typed JSON; these narrow the columns / sort /
// group props into the typed shapes ProductTableBlock expects, dropping
// anything malformed rather than throwing (same forgiving stance as the rest
// of the dispatcher).

function coerceColumns(raw: unknown): ColumnDef[] {
  if (!Array.isArray(raw)) return [];
  const out: ColumnDef[] = [];
  for (const c of raw) {
    if (typeof c !== "object" || c === null) continue;
    const o = c as Record<string, unknown>;
    out.push({
      header: typeof o.header === "string" ? o.header : undefined,
      headerSource: o.headerSource as ContentSource | undefined,
      align: o.align === "center" || o.align === "right" ? o.align : "left",
      widthPx: typeof o.widthPx === "number" ? o.widthPx : undefined,
      blocks: Array.isArray(o.blocks) ? (o.blocks as BlockInstance[]) : [],
    });
  }
  return out;
}

function coerceSort(raw: unknown): SortRule[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: SortRule[] = [];
  for (const r of raw) {
    if (typeof r !== "object" || r === null) continue;
    const o = r as Record<string, unknown>;
    if (typeof o.by !== "string" || o.by === "") continue;
    out.push({ by: o.by, dir: o.dir === "desc" ? "desc" : "asc" });
  }
  return out.length > 0 ? out : undefined;
}

function coerceGroup(raw: unknown): GroupConfig | undefined {
  if (typeof raw !== "object" || raw === null) return undefined;
  const o = raw as Record<string, unknown>;
  if (typeof o.by !== "string" || o.by === "") return undefined;
  return {
    by: o.by,
    dir: o.dir === "desc" ? "desc" : "asc",
    showHeader: o.showHeader !== false,
    headerLabelPrefix: typeof o.headerLabelPrefix === "string" ? o.headerLabelPrefix : undefined,
    headerBlocks: Array.isArray(o.headerBlocks) ? (o.headerBlocks as BlockInstance[]) : undefined,
  };
}

/** Stable fragment re-export to keep imports tidy in callers that want one. */
export type { Fragment };
